import { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';

import { GridColNumber } from '@shared/components/grid/GridColNumber';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { gridBooleanCell, gridCurrencyCell, dataGridCol } from '@shared/components/grid/common';
import { incomeTypes } from '@shared/constants/lists';
import { IMap } from '@shared/interfaces';
import { NContact } from '../../validation';
import { ContactSection } from '../ContactSection';
import { otherIncomeValidationSchema } from './validation';

interface IOtherIncomeProps {
	data?: NContact.OtherIncome[];
	mutateCombinedDti?: () => void;
	onChange(data: NContact.OtherIncome[]): Promise<IMap<string> | undefined>;
}

export const OtherIncome = ({ data = [], mutateCombinedDti, onChange }: IOtherIncomeProps) => {
	const rows = useMemo(
		() =>
			data.map((row) => ({
				...row,
				yrs: Math.floor((row.monthsAtJob || 0) / 12),
				mths: Math.floor((row.monthsAtJob || 0) % 12),
			})).sort((a, b) => Number(b.isCurrent) - Number(a.isCurrent)),
		[data],
	);

	const columns: GridColDef[] = useMemo(
		() =>
			[
				dataGridCol('isCurrent', {
					headerName: 'Current',
					minWidth: 100,
					flex: 1,
					headerAlign: 'left',
					align: 'left',
					...gridBooleanCell,
				}),
				dataGridCol('incomeType', {
					minWidth: 250,
					flex: 1,
					renderCell: (params) => <div>{incomeTypes.find((type) => type.value === params.value)?.label}</div>,
					renderEditCell: (params) => <GridColSelect isSearchable canBeClean={false} options={incomeTypes} params={params} />,
				}),
				dataGridCol('monthlyAmount', { ...gridCurrencyCell, minWidth: 150, flex: 1 }),
				dataGridCol('yrs', {
					minWidth: 100,
					flex: 1,
					renderEditCell: (params) => <GridColNumber params={params} limit={2} />,
				}),
				dataGridCol('mths', {
					minWidth: 100,
					flex: 1,
					renderEditCell: (params) => <GridColNumber params={params} limit={2} />,
				}),
				dataGridCol('monthsAtJob', {
					hide: true,
					valueSetter: ({ row }) => ({
						...row,
						monthsAtJob: (+row.yrs || 0) * 12 + (+row.mths || 0),
					}),
				}),
			].map((i) => ({ editable: true, sortable: false, disableColumnMenu: true, ...i })),
		[],
	);

	return (
		<ContactSection
			strict
			name="Other Income"
			buttonTitle="+ New"
			data={rows}
			columns={columns}
			validateSchema={otherIncomeValidationSchema}
			mutateCombinedDti={mutateCombinedDti}
			onChange={onChange}
		/>
	);
};
